import createApp from '@shopify/app-bridge/index.js';
import { Redirect } from '@shopify/app-bridge/actions/index.js';
import { getSessionToken } from '@shopify/app-bridge-utils/index.js';

const getApiKey = () => import.meta.env.VITE_SHOPIFY_API_KEY as string | undefined;

const appCache: Record<string, any> = {};

export const getAppBridge = (host: string) => {
  const apiKey = getApiKey();
  if (!apiKey || !host) return null;
  if (!appCache[host]) {
    appCache[host] = createApp({ apiKey, host, forceRedirect: true });
  }
  return appCache[host];
};

export const redirectToAuth = (host: string, reauthorizeUrl: string): boolean => {
  const apiKey = getApiKey();
  if (!apiKey || !host || !reauthorizeUrl) {
    return false;
  }

  try {
    const app = getAppBridge(host);
    if (!app) return false;
    const redirect = Redirect.create(app);
    const absoluteUrl = new URL(reauthorizeUrl, window.location.origin).toString();
    redirect.dispatch(Redirect.Action.REMOTE, {
      url: absoluteUrl,
      newContext: false,
    });
    return true;
  } catch {
    return false;
  }
};

export const redirectToExternal = (
  host: string,
  url: string,
  newContext = true
): boolean => {
  const apiKey = getApiKey();
  if (!apiKey || !host || !url) {
    return false;
  }

  try {
    const app = getAppBridge(host);
    if (!app) return false;
    const redirect = Redirect.create(app);
    redirect.dispatch(Redirect.Action.REMOTE, {
      url,
      newContext,
    });
    return true;
  } catch {
    return false;
  }
};

export const fetchWithSessionToken = async (
  host: string,
  url: string,
  options: RequestInit = {}
) => {
  const app = getAppBridge(host);
  if (!app) {
    return fetch(url, options);
  }
  const token = await getSessionToken(app);
  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(url, { ...options, headers });
};
